import { DataSource } from "@seriously-rad/seriouslyrad-framework-core/Data/DataSource";
import { Nullable, PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { nameof } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/Extensions";
import { TrueEvaluator } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/TrueEvaluator";
import { ButtonStyleEnum } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ButtonStyleEnum";
import { ControlRef } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ControlRef";
import { Dialog, DialogPropertyValues } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Dialog";
import { PropertyEditorNew } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/PropertyEditorNew";
import { TextBox, TextBoxTypeEnum } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/TextBox";
import { ValidationPanel } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ValidationPanel";
import { DialogResultEnum } from "@seriously-rad/seriouslyrad-framework-core/Ui/Dialogs/DialogResultEnum";
import { OfficerRequest } from "../../Server/Schema/Entities/OfficerRequest.Generated";
import { NAMESPACE } from "./_Namespace";

import "./OfficerRequestImportDialog.scss";

interface IField
{
    dataSourceProperty: string;
    label: string;
    valueConverter?: (value: string) => Nullable<string>;
}

interface IFields
{
    [field: string]: IField;
}

interface ISection
{
    label: string;
    fields: IFields;
}

interface ISections
{
    [section: string]: ISection;
}

const
    FORM_SECTIONS: ISections = {
        applicantInformation: {
            label: "Applicant Information",
            fields: {
                name: {
                    dataSourceProperty: nameof(OfficerRequest, "applicantName"),
                    label: "Select",
                },
                emailAddress: {
                    dataSourceProperty: nameof(OfficerRequest, "applicantEmailAddress"),
                    label: "Email",
                },
                contactNumber: {
                    dataSourceProperty: nameof(OfficerRequest, "applicantContactNumber"),
                    label: "Contact No.",
                },
            },
        },
        unitInformation: {
            label: "Unit Information",
            fields: {
                unitType: {
                    dataSourceProperty: nameof(OfficerRequest, "unitTypeLookupItemId"),
                    label: "Unit type",
                    valueConverter: value =>
                    {
                        switch (value?.toLowerCase())
                        {
                            case "chapter":
                                return "9e2ef10e-128d-4613-8045-bc46f8a1f62b";
                            case "lodge":
                                return "78f4b3f3-5dd9-4906-9ecc-5d416d68933d";
                            default:
                                return null;
                        }
                    },
                },
                unitName: {
                    dataSourceProperty: nameof(OfficerRequest, "unitName"),
                    label: "Unit Name",
                },
                unitNumber: {
                    dataSourceProperty: nameof(OfficerRequest, "unitNumber"),
                    label: "Unit Number",
                },
                contactName: {
                    dataSourceProperty: nameof(OfficerRequest, "unitContactName"),
                    label: "Contact Name",
                },
                contactOffice: {
                    dataSourceProperty: nameof(OfficerRequest, "unitContactOffice"),
                    label: "Contact - Office",
                },
                contactEmailAddress: {
                    dataSourceProperty: nameof(OfficerRequest, "unitContactEmailAddress"),
                    label: "Contact - email address",
                },
                contactPhoneNumber: {
                    dataSourceProperty: nameof(OfficerRequest, "unitContactNumber"),
                    label: "Contact - phone no",
                },
            },
        },
        meetingInformation: {
            label: "Meeting Information",
            fields: {
                date: {
                    dataSourceProperty: nameof(OfficerRequest, "meetingDate"),
                    label: "Date",
                    valueConverter: value =>
                    {
                        if (value)
                        {
                            const
                                regexDate = /([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})/gi,
                                matches = regexDate.exec(value);

                            if (matches && matches.length === 4)
                                return `${matches[3]}-${matches[2].padStart(2, "0")}-${matches[1].padStart(2, "0")}`;
                        }

                        return null;
                    },
                },
                day: {
                    dataSourceProperty: nameof(OfficerRequest, "meetingDay"),
                    label: "Day",
                },
                time: {
                    dataSourceProperty: nameof(OfficerRequest, "meetingTime"),
                    label: "Time",
                },
                venue: {
                    dataSourceProperty: nameof(OfficerRequest, "meetingVenue"),
                    label: "Venue",
                },
                purposeOfVipAttendance: {
                    dataSourceProperty: nameof(OfficerRequest, "meetingPurposeOfVipAttendance"),
                    label: "Purpose of VIP attendance",
                },
                otherRelevantDetails: {
                    dataSourceProperty: nameof(OfficerRequest, "meetingOtherRelevantDetails"),
                    label: "Other relevant details ",
                },
                nameOrRankOfAnyOtherSeniorGuests: {
                    dataSourceProperty: nameof(OfficerRequest, "meetingNameOrRankOfAnyOtherSeniorGuests"),
                    label: "Name/Rank of any other senior guest(s)",
                },
            }
        },
        diningInformation: {
            label: "Dining Information",
            fields: {
                venue: {
                    dataSourceProperty: nameof(OfficerRequest, "diningVenue"),
                    label: "Venue",
                },
                time: {
                    dataSourceProperty: nameof(OfficerRequest, "diningTime"),
                    label: "Time",
                },
                cost: {
                    dataSourceProperty: nameof(OfficerRequest, "diningCost"),
                    label: "Cost",
                },
            }
        },
        supportRequired: {
            label: "Support Required",
            fields: {
                type: {
                    dataSourceProperty: nameof(OfficerRequest, "supportTypeLookupItemId"),
                    label: "Select",
                    valueConverter: value =>
                    {
                        switch (value?.toLowerCase())
                        {
                            case "escorting officer":
                                return "ed6a23da-d960-44a6-aa66-270391cb34b4";
                            case "escorting officer & stewards":
                                return "6f4b9e17-b183-4d95-ba5f-69ed154a3f85";
                            case "stewards":
                                return "8d5fd3e6-5bcd-47d9-b69f-f2e1b0469ccb";
                            default:
                                return null;
                        }
                    },
                }
            }
        },
    };

export class OfficerRequestImportDialog extends Dialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "OfficerRequestImportDialog";
    public static override className = "OfficerRequestImportDialog";

    // #region Overrides

    // #region Methods

    public static override createPropertyDefaults(): PartialNullable<DialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                buttonStyle: ButtonStyleEnum.okCancel,
                okButtonText: "Import",
                scrollable: false,
                title: "Officer Request Import",
            }
        };
    }

    // #endregion

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _propertyEditorRequestRef?: ControlRef<PropertyEditorNew>;
    protected _validationPanelControlRef?: ControlRef<ValidationPanel>;

    // #endregion

    // #region Constructors

    constructor(propertyValues?: PartialNullable<DialogPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("eoc-officer-request-import-dialog");
    }

    // #endregion

    // #region Protected Methods

    protected _convertEmailTextToOfficerRequest()
    {
        const
            dataSource = this.dataSource.get(),
            emailText = dataSource?.propertyValueByPathGet<string>("emailText");

        if (!emailText)
            throw new Error("Request is required");

        const
            lines =
                emailText
                    .replaceAll("\r\n", "\n")
                    .replaceAll("\r", "\n")
                    .split("\n")
                    .map(item => item.trim())
                    .filter(item => !!item);

        let
            currentSection: ISection | undefined,
            currentField: IField | undefined;

        const
            fieldMap: Map<IField, string> = new Map();

        for (const line of lines)
        {
            let
                sectionChanged = false;

            for (const section of (Object.keys(FORM_SECTIONS) as (keyof typeof FORM_SECTIONS)[]))
            {
                if (line.startsWith(FORM_SECTIONS[section].label))
                {
                    currentSection = FORM_SECTIONS[section];
                    currentField = undefined;
                    sectionChanged = true;
                    break;
                }
            }

            if (sectionChanged)
                continue;

            if (!currentSection)
                continue;

            let
                fieldChanged = false;

            for (const field of (Object.keys(currentSection.fields) as (keyof typeof currentSection.fields)[]))
            {
                if (line.startsWith(currentSection.fields[field].label))
                {
                    currentField = currentSection.fields[field];
                    fieldChanged = true;
                    break;
                }
            }

            if (fieldChanged)
                continue;

            if (!currentField)
                continue;

            if (line.startsWith("⚪"))
                continue;

            let
                fieldValue = fieldMap.get(currentField),
                cleanLine =
                    line
                        .replaceAll("⮛", StringHelper.empty)
                        .replaceAll("⚫", StringHelper.empty)
                        .trim();

            if (fieldValue === undefined)
                fieldValue = cleanLine;
            else
                fieldValue += `\n${cleanLine}`;

            fieldMap.set(currentField, fieldValue);
        }

        const
            data: any = {};

        for (let [key, value] of fieldMap)
            data[key.dataSourceProperty] = key.valueConverter ? key.valueConverter(value) : value;

        return data;
    }
    protected _validate()
    {
        this._validationPanelControlRef?.current?.setErrorMessages(null);
        this._propertyEditorRequestRef?.current?.errorState.set(false);

        const
            dataSource = this.dataSource.get(),
            emailText = dataSource?.propertyValueByPathGet<string>("emailText"),
            errorMessages = [];

        if (!emailText)
        {
            this._propertyEditorRequestRef?.current?.errorState.set(true);
            errorMessages.push("Email text is required");
        }

        const
            hasErrors = errorMessages.length > 0;

        if (hasErrors)
            this._validationPanelControlRef?.current?.setErrorMessages(errorMessages);

        return !hasErrors;
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: DialogPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._propertyEditorRequestRef = new ControlRef();
        this._validationPanelControlRef = new ControlRef();
    }

    protected override _createChildrenHtml()
    {
        return (
            <>
                <ValidationPanel ref={this._validationPanelControlRef} />
                <PropertyEditorNew ref={this._propertyEditorRequestRef} cssClass="eoc-email-text-property-editor" dataSourceProperty="emailText" dataSourcePropertyType="System.String, System.Private.CoreLib" editMode={true} editorControl={new TextBox({ cssClass: "eoc-email-text-textbox", type: TextBoxTypeEnum.multiline })} propertyDisplayName="Email Text" mandatoryEvaluator={new TrueEvaluator()} />
            </>
        );
    }

    public override async closeAsync()
    {
        if (this.dialogResult === DialogResultEnum.ok)
        {
            if (!this._validate())
                return;

            this.promiseData = this._convertEmailTextToOfficerRequest();
        }

        await super.closeAsync();
    }
    public override async openAsync()
    {
        this.dataSource.set(new DataSource({ data: {} }));

        return await super.openAsync();
    }

    // #endregion

    // #endregion
}